import React, { useEffect, useRef, useState } from 'react';
import { useResendEmailVerificationCode } from '../../hooks/apolloHooks/useResendEmailVerificationCode';
import { useVerifyEmail } from '../../hooks/apolloHooks/useVerifyEmail';
import { CheckmarkCircleIcon, InfoIconOutlined } from '../svgs/ExpressonSVGs';
import Tooltip from './Tooltip';

type VerifyEmailProps = {
    email: string;
    emailId: number;
    onVerified?: (email: string) => void;
    onVerifySuccess?: () => void;
}

/**
 * VerifyEmail
 *
 * Reusable email verification component. Displays the email address (read-only),
 * lets the user enter the verification code sent to that address, and calls
 * `onVerified` when done. Shows a "verifierad" confirmation for 3 seconds then
 * clears itself.
 *
 * The parent is responsible for triggering the initial code send before rendering
 * this component, or the user can use the "Skicka verifieringskod igen" link.
 */
const VerifyEmail: React.FC<VerifyEmailProps> = ({ email, emailId, onVerified, onVerifySuccess }) => {
    const [codeValue, setCodeValue] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    const codeInputRef = useRef<HTMLInputElement>(null);
    const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const { verifyEmail, loading: verifyLoading } = useVerifyEmail();
    const { resendCode } = useResendEmailVerificationCode();

    // Auto-focus code input on mount
    useEffect(() => {
        setTimeout(() => codeInputRef.current?.focus(), 0);
    }, []);

    // Cleanup cooldown on unmount
    useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, []);

    // After verification success, notify parent after 3 seconds
    useEffect(() => {
        if (!isVerified) return;
        const timeout = setTimeout(() => {
            onVerified?.(email);
        }, 3000);
        return () => clearTimeout(timeout);
    }, [isVerified]);

    const handleVerify = async () => {
        if (!codeValue || verifyLoading) return;
        setSubmitError(null);
        try {
            const result = await verifyEmail({ emailId, verificationCode: codeValue });
            if (result) {
                setIsVerified(true);
                onVerifySuccess?.();
            }
        } catch {
            setSubmitError('Felaktig verifieringskod. Kontrollera koden och försök igen.');
            setCodeValue('');
        }
    };

    const handleResend = async () => {
        if (cooldownSeconds > 0) return;
        try {
            await resendCode({ emailId });
        } catch {
            // Silent fail — user can try again
        }
        setCooldownSeconds(30);
        cooldownRef.current = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current!);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && codeValue.length === 4) {
            handleVerify();
        }
    };

    if (isVerified) {
        return (
            <div className="verify-email verify-email--success">
                <CheckmarkCircleIcon width={28} height={28} />
                <span>E-post: {email} verifierad</span>
            </div>
        );
    }

    return (
        <div className="verify-email">
            <div className="verify-email__address">
                <h6 className="body-text">Verifiera e-post nedan för att fortsätta</h6>
                <p className="info-text">
                    Verifieringen behöver göras omgående.
                </p>
                <span className="verify-email__label">E-post:</span>
                <span className="verify-email__value">{email}</span>
            </div>

            <div className="input-wrapper verification">
                <label htmlFor="verify-email-code">
                    Verifieringskod
                    <Tooltip
                        position="top-end"
                        text="Om du inte har fått någon kod, kontrollera din e-post, skräppostmapp eller klicka på 'Skicka verifieringskod igen'."
                    >
                        <InfoIconOutlined width={25} height={26} />
                    </Tooltip>
                </label>
                <input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    id="verify-email-code"
                    name="verificationCode"
                    placeholder="Ange verifieringskoden"
                    maxLength={4}
                    value={codeValue}
                    onChange={e => setCodeValue(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={handleKeyDown}
                />
                <span>Fyll i koden du fick i mejlet för att verifiera din e-postadress.</span>
                <span
                    className={`resend-verification${cooldownSeconds > 0 ? ' resend-verification--cooldown' : ''}`}
                    onClick={handleResend}
                    tabIndex={cooldownSeconds > 0 ? -1 : 0}
                    onKeyDown={(e) => { if (cooldownSeconds === 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleResend(); } }}
                >
                    {cooldownSeconds > 0
                        ? `Skicka igen om ${cooldownSeconds}s`
                        : 'Skicka verifieringskod igen'
                    }
                </span>
            </div>

            {submitError && (
                <span className="input-error">{submitError}</span>
            )}

            <button
                type="button"
                className="button-edit body-text"
                disabled={codeValue.length < 4 || verifyLoading}
                onClick={handleVerify}
            >
                {verifyLoading ? 'Verifierar...' : 'Verifiera e-post'}
            </button>
        </div>
    );
};

export default VerifyEmail;
